import { fromJson, toJson } from '@bufbuild/protobuf'
import { type Timestamp, TimestampSchema } from '@bufbuild/protobuf/wkt'

/**
 * リリース告知の未読判定 (高水位方式)。
 *
 * 既読は「読んだ告知のうち最も新しい `published_at`」1 つだけで持ち、記事ごとの
 * 既読集合は持たない (map #8571 D)。比較は `Timestamp` の seconds / nanos で行う。
 */

/** a が b より後なら正、同時なら 0、前なら負。 */
function compareTimestamp(a: Timestamp, b: Timestamp): number {
  if (a.seconds !== b.seconds) return a.seconds > b.seconds ? 1 : -1
  return a.nanos - b.nanos
}

/**
 * 告知のうち最も新しい `publishedAt`。告知 0 件なら undefined。
 *
 * backend は published_at 降順で返すが、先頭を取らずに最大値を取る
 * (並び順の契約が崩れても未読判定が古い値で止まらないようにするため)。
 */
export function latestPublishedAt(
  announcements: ReadonlyArray<{ publishedAt?: Timestamp }>,
): Timestamp | undefined {
  let latest: Timestamp | undefined
  for (const { publishedAt } of announcements) {
    if (publishedAt === undefined) continue
    if (latest === undefined || compareTimestamp(publishedAt, latest) > 0) {
      latest = publishedAt
    }
  }
  return latest
}

/**
 * candidate が既読の記録 current より新しいか。candidate が無ければ偽、current が
 * 無ければ (記録なし) 真。既読の書き込み可否と、保存先 2 つのどちらを採るかに使う。
 */
export function isNewerReadMark(
  candidate: Timestamp | undefined,
  current: Timestamp | undefined,
): boolean {
  if (candidate === undefined) return false
  if (current === undefined) return true
  return compareTimestamp(candidate, current) > 0
}

/**
 * 未読があるか。告知が無ければ未読なし、既読の記録が無ければ (初めて使うブラウザ)
 * 告知が 1 件以上ある時点で未読とする (design `version-info.js` の初回訪問の扱い)。
 */
export function hasUnreadAnnouncement(
  latest: Timestamp | undefined,
  lastRead: Timestamp | undefined,
): boolean {
  return isNewerReadMark(latest, lastRead)
}

/** 既読の記録を保存用の文字列 (protobuf JSON = RFC3339) にする。 */
export function encodeReadMark(publishedAt: Timestamp): string {
  return toJson(TimestampSchema, publishedAt)
}

/** 保存文字列を既読の記録に戻す。未保存・壊れた値は「記録なし」として undefined。 */
export function decodeReadMark(raw: string | undefined): Timestamp | undefined {
  if (raw === undefined) return undefined
  try {
    return fromJson(TimestampSchema, raw)
  } catch {
    return undefined
  }
}
